import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CalendarPreview from './CalendarPreview';
import DayColumnViz   from './DayColumnViz';
import Footer         from './Footer';
import { TIMEZONES, CATEGORIES, convertTime, formatTime } from '../lib/constants';
import { getPoll, getVotes, upsertVotes } from '../lib/supabase';

function GalacticTitle() {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate('/')}
      style={{
        fontSize: 11, letterSpacing: 2, color: 'var(--muted)',
        textTransform: 'uppercase', fontFamily: 'var(--font-head)',
        marginBottom: 8, cursor: 'pointer', display: 'inline-block',
        background: 'none', border: 'none', padding: 0,
      }}
      aria-label="Galactic Meeting — create a new poll"
    >
      ✦ Galactic Meeting
    </button>
  );
}

// vote_type values: true = yes, false = no, 'ifneedbe' = if need be
const VOTE_OPTIONS = [
  { value: true,        label: '✓ Yes',        cls: 'vote-yes' },
  { value: 'ifneedbe',  label: '~ If need be', cls: 'vote-ifneedbe' },
  { value: false,       label: '✗ No',         cls: 'vote-no' },
];

export default function ParticipantFlow() {
  const { pollId } = useParams();

  const [poll, setPoll]               = useState(null);
  const [allVotes, setAllVotes]       = useState([]);
  const [name, setName]               = useState('');
  const [nameSet, setNameSet]         = useState(false);
  const [votes, setVotes]             = useState({});
  const [timezone, setTimezone]       = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  );
  const [resultsTz, setResultsTz]     = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  );
  const [calMode, setCalMode]         = useState('abstract');
  const [calYear, setCalYear]         = useState(new Date().getFullYear());
  const [calMonth, setCalMonth]       = useState(new Date().getMonth());
  const [hoveredOpt, setHoveredOpt]   = useState(null);
  const [activeTab, setActiveTab]     = useState('vote');
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  const [error, setError]             = useState('');
  const [use12h, setUse12h]           = useState(true);
  const [pinnedParticipants, setPinnedParticipants] = useState(new Set());

  useEffect(() => {
    async function load() {
      try {
        const [p, v] = await Promise.all([getPoll(pollId), getVotes(pollId)]);
        setPoll(p);
        setAllVotes(v);
      } catch (e) {
        setError('not_found');
      }
    }
    load();
  }, [pollId]);

  const options = useMemo(() => {
    if (!poll) return [];
    const rows = poll._rows || poll.rows || [];
    const mapped = (poll.rules_json || []).map((r, i) => {
      const row = rows.find(rw => rw.id === r.rowId);
      return { ...r, index: i, row, baseLabel: row ? row.label + ' at ' + r.time : 'Option ' + (i + 1) };
    });

    // Sort: group by row (preserving first-appearance order of each row),
    // then within each row sort chronologically by time string "HH:MM"
    const rowOrder = [];
    const seen = new Set();
    mapped.forEach(o => {
      const key = o.rowId || '__none__';
      if (!seen.has(key)) { seen.add(key); rowOrder.push(key); }
    });

    return [...mapped].sort((a, b) => {
      const aRow = rowOrder.indexOf(a.rowId || '__none__');
      const bRow = rowOrder.indexOf(b.rowId || '__none__');
      if (aRow !== bRow) return aRow - bRow;
      // Same row — sort by time
      return a.time.localeCompare(b.time);
    });
  }, [poll]);

  const displayLabel = (opt) => {
    if (!poll || timezone === poll.timezone) {
      return opt.row
        ? opt.row.label + ' at ' + formatTime(opt.time, use12h)
        : opt.baseLabel;
    }
    const { time, dayShift } = convertTime(opt.time, poll.timezone, timezone);
    const dayNote = dayShift === 1 ? ' (+1 day)' : dayShift === -1 ? ' (−1 day)' : '';
    return opt.row
      ? opt.row.label + ' at ' + formatTime(time, use12h) + dayNote
      : opt.baseLabel;
  };

  const resultsLabel = (opt) => {
    if (!poll || resultsTz === poll.timezone) {
      return opt.row
        ? opt.row.label + ' at ' + formatTime(opt.time, use12h)
        : opt.baseLabel;
    }
    const { time, dayShift } = convertTime(opt.time, poll.timezone, resultsTz);
    const dayNote = dayShift === 1 ? ' (+1 day)' : dayShift === -1 ? ' (−1 day)' : '';
    return opt.row
      ? opt.row.label + ' at ' + formatTime(time, use12h) + dayNote
      : opt.baseLabel;
  };

  // Tally helpers — vote_type is true, false, or 'ifneedbe'
  const yesFor       = idx => allVotes.filter(v => v.rule_index === idx && v.vote_type === true);
  const noFor        = idx => allVotes.filter(v => v.rule_index === idx && v.vote_type === false);
  const ifNeedBeFor  = idx => allVotes.filter(v => v.rule_index === idx && v.vote_type === 'ifneedbe');
  // Participants who voted on at least one option but left this one blank
  const didntVoteFor = idx => {
    const votedOnThis = new Set(allVotes.filter(v => v.rule_index === idx).map(v => v.participant_name));
    return participants.filter(p => !votedOnThis.has(p));
  };

  const maxYes = useMemo(() => {
    if (!options.length) return 0;
    return Math.max(...options.map(opt => yesFor(opt.index).length));
  }, [options, allVotes]);

  // Returns true if all pinned participants said yes (or if none pinned)
  const pinnedAllYes = (idx) => {
    if (pinnedParticipants.size === 0) return false;
    const yesNames = new Set(yesFor(idx).map(v => v.participant_name));
    for (const p of pinnedParticipants) {
      if (!yesNames.has(p)) return false; // covers no, if-need-be, and didn't vote
    }
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await upsertVotes(pollId, name, votes);
      const fresh = await getVotes(pollId);
      setAllVotes(fresh);
      setSubmitted(true);
      setActiveTab('results');
    } catch (e) {
      setError('submit_failed');
    } finally {
      setSubmitting(false);
    }
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  const participants = [...new Set(allVotes.map(v => v.participant_name))];

  // The option currently shown in the sidebar — hovered if set, else first valid option
  const firstValidOpt = useMemo(() => options.find(o => o.row), [options]);
  const displayOpt = hoveredOpt || firstValidOpt || null;

  // Build voterData for DayColumnViz: { [time]: { yes, no, ifNeedBe, didntVote } }
  const voterDataForRow = useMemo(() => {
    if (!displayOpt) return null;
    const rowOpts = options.filter(o => o.row && o.row.id === displayOpt.row?.id);
    const data = {};
    rowOpts.forEach(opt => {
      data[opt.time] = {
        yes:       yesFor(opt.index).map(v => v.participant_name),
        no:        noFor(opt.index).map(v => v.participant_name),
        ifNeedBe:  ifNeedBeFor(opt.index).map(v => v.participant_name),
        didntVote: didntVoteFor(opt.index),
      };
    });
    return data;
  }, [displayOpt, options, allVotes]);

  // ── Error / loading ────────────────────────────────────────────────────────
  if (error === 'not_found') {
    return (
      <main id="main-content" style={{
        textAlign: 'center', padding: '80px 32px',
        position: 'relative', zIndex: 1, maxWidth: 600, margin: '0 auto',
      }}>
        <div style={{ fontSize: 56, marginBottom: 24 }} aria-hidden="true">🌌</div>
        <h1 style={{ marginBottom: 16 }}>Poll not found</h1>
        <p style={{
          color: 'var(--muted)', fontSize: 17, lineHeight: 1.8,
          fontStyle: 'italic',
        }}>
          You have reached the Great Barrier at the edge of the galaxy where there are no meetings.
          May you bring with you some of the peace from out here back with you on your journey home.
        </p>
        <button
          className="btn-ghost"
          style={{ marginTop: 36 }}
          onClick={() => window.location.href = '/'}
        >
          ← Return home
        </button>
      </main>
    );
  }

  if (error === 'submit_failed') {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--red)', position: 'relative', zIndex: 1 }}>
        Failed to submit votes. Please try again.
      </div>
    );
  }

  if (!poll) return (
    <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--muted)', position: 'relative', zIndex: 1 }}>
      Loading…
    </div>
  );

  const catLabel = CATEGORIES.find(c => c.id === poll.category)?.label || poll.category;
  const duration = poll.duration || 60;

  return (
    <main id="main-content" style={{ maxWidth: 1000, margin: '0 auto', padding: '36px 24px', position: 'relative', zIndex: 1 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 36 }}>
        <div>
          <GalacticTitle />
          <h1 style={{ fontSize: '1.8rem', marginBottom: 8 }}>{poll.title}</h1>
          {poll.instructions && (
            <p style={{ color: 'var(--muted)', fontSize: 14, maxWidth: 500, marginBottom: 12 }}>{poll.instructions}</p>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span className="pill" style={{ background: 'rgba(124,111,247,0.12)', color: 'var(--accent)', border: '1px solid rgba(124,111,247,0.2)' }}>
              {catLabel}
            </span>
            <span className="pill" style={{ background: 'var(--surface2)', color: 'var(--muted)', border: '1px solid var(--border)' }}>
              Organizer: {poll.timezone}
            </span>
            <span className="pill" style={{ background: 'var(--surface2)', color: 'var(--muted)', border: '1px solid var(--border)' }}>
              {duration} min meeting
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <div role="tablist" aria-label="Poll sections" className="toggle-wrap">
            <button role="tab" aria-selected={activeTab === 'vote'} className={'toggle-opt' + (activeTab === 'vote' ? ' active' : '')} onClick={() => setActiveTab('vote')}>Vote</button>
            <button role="tab" aria-selected={activeTab === 'results'} className={'toggle-opt' + (activeTab === 'results' ? ' active' : '')} onClick={() => setActiveTab('results')}>
              Results {participants.length > 0 && '(' + participants.length + ')'}
            </button>
          </div>
          {/* 12h / 24h toggle */}
          <div className="toggle-wrap" role="group" aria-label="Time format">
            <button className={'toggle-opt' + (use12h  ? ' active' : '')} onClick={() => setUse12h(true)}  aria-pressed={use12h}  style={{ fontSize: 11, padding: '4px 10px' }}>12h</button>
            <button className={'toggle-opt' + (!use12h ? ' active' : '')} onClick={() => setUse12h(false)} aria-pressed={!use12h} style={{ fontSize: 11, padding: '4px 10px' }}>24h</button>
          </div>
        </div>
      </div>

      {/* ── VOTE TAB ────────────────────────────────────────────────────────── */}
      {activeTab === 'vote' && (
        <div className={nameSet ? 'poll-layout' : ''}>
          <div>
            {/* Name gate */}
            {!nameSet ? (
              <div className="card fade-up">
                <h3 style={{ marginBottom: 16 }}>Enter your name to vote</h3>
                <div className="name-gate-row">
                  <input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Your name"
                    onKeyDown={e => e.key === 'Enter' && name.trim() && setNameSet(true)}
                    autoFocus
                  />
                  <button className="btn-primary" style={{ whiteSpace: 'nowrap' }} disabled={!name.trim()} onClick={() => setNameSet(true)}>
                    Start voting
                  </button>
                </div>
              </div>
            ) : (
              <div className="fade-up">
                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
                  <div style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span>Voting as <strong>{name}</strong></span>
                    <button
                      onClick={() => {
                        // Re-edit: reload existing votes for this person into local state
                        const existing = {};
                        allVotes.filter(v => v.participant_name === name).forEach(v => {
                          existing[v.rule_index] = v.vote_type;
                        });
                        setVotes(existing);
                        setSubmitted(false);
                      }}
                      style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                    >
                      edit my votes
                    </button>
                    <span style={{ color: 'var(--border2)', fontSize: 11 }}>·</span>
                    <button
                      onClick={() => { setNameSet(false); setName(''); setVotes({}); setSubmitted(false); }}
                      style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                    >
                      vote as someone else
                    </button>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <span style={{ color: 'var(--muted)' }}>Your timezone:</span>
                    <select value={timezone} onChange={e => setTimezone(e.target.value)}
                      style={{ width: 'auto', fontSize: 12, padding: '6px 10px' }}>
                      {!TIMEZONES.find(tz => tz.value === timezone) && (
                        <option value={timezone}>{timezone}</option>
                      )}
                      {TIMEZONES.map(tz => (
                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                      ))}
                    </select>
                  </label>
                </div>

                {/* Option cards */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                  {options.map((opt) => {
                    const idx      = opt.index;
                    const yVotes   = yesFor(idx);
                    const nVotes   = noFor(idx);
                    const inbVotes = ifNeedBeFor(idx);
                    const dnvNames = didntVoteFor(idx);
                    const myVote   = votes[idx];
                    const isTop    = yVotes.length === maxYes && maxYes > 0;
                    const total    = participants.length;
                    const yesPct   = total > 0 ? Math.round(yVotes.length / total * 100) : 0;

                    return (
                      <div
                        key={idx}
                        onMouseEnter={() => setHoveredOpt(opt)}
                        onMouseLeave={() => setHoveredOpt(null)}
                        style={{
                          padding: '14px 16px', borderRadius: 12,
                          border: '1px solid ' + (isTop ? 'var(--green)' : myVote === true ? 'var(--accent)' : myVote === false ? 'rgba(255,77,106,0.3)' : 'var(--border2)'),
                          background: isTop ? 'rgba(68,215,168,0.03)' : 'var(--surface2)',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 3 }}>{displayLabel(opt)}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                              {yVotes.length} yes · {inbVotes.length} if need be · {nVotes.length} no{dnvNames.length > 0 ? ' · ' + dnvNames.length + ' no response' : ''}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                            {isTop && <span className="tag tag-winner">⭐️</span>}
                            {VOTE_OPTIONS.map(vo => {
                              const isActive = myVote === vo.value;
                              return (
                                <button
                                  key={String(vo.value)}
                                  className={'vote-btn ' + vo.cls + (isActive ? ' active' : '')}
                                  onClick={() => setVotes(v => {
                                    const next = Object.assign({}, v);
                                    if (next[idx] === vo.value) delete next[idx];
                                    else next[idx] = vo.value;
                                    return next;
                                  })}
                                >
                                  {vo.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        {total > 0 && (
                          <div className="heatmap-track" title={yesPct + '% yes of ' + total + ' participant' + (total !== 1 ? 's' : '')}>
                            <div className="heatmap-fill" style={{
                              width: yesPct + '%',
                              background: isTop ? 'var(--green)' : 'var(--accent)',
                            }} />
                            {dnvNames.length > 0 && (
                              <div className="heatmap-fill" style={{
                                width: Math.round(dnvNames.length / total * 100) + '%',
                                background: 'var(--border2)',
                                opacity: 0.6,
                              }} />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <button
                  className="btn-primary"
                  style={{ width: '100%' }}
                  disabled={Object.keys(votes).length === 0 || submitting}
                  onClick={handleSubmit}
                >
                  {submitting ? 'Submitting…' : 'Submit my votes →'}
                </button>
                {submitted && (
                  <p aria-live="polite" style={{ color: 'var(--green)', fontSize: 13, textAlign: 'center', marginTop: 12 }}>
                    ✓ Votes recorded! Switched to results view.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Sidebar — only shown after name is entered */}
          {nameSet && (
          <div className="poll-sidebar">
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>Pattern preview</span>
                <div className="toggle-wrap" role="group" aria-label="Calendar view mode">
                  <button className={'toggle-opt' + (calMode === 'abstract' ? ' active' : '')} onClick={() => setCalMode('abstract')} aria-pressed={calMode === 'abstract'} style={{ fontSize: 11, padding: '4px 8px' }}>Abstract</button>
                  <button className={'toggle-opt' + (calMode === 'real'     ? ' active' : '')} onClick={() => setCalMode('real')}     aria-pressed={calMode === 'real'}     style={{ fontSize: 11, padding: '4px 8px' }}>Real</button>
                </div>
              </div>
              {calMode === 'real' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <button className="btn-ghost" style={{ padding: '4px 8px', fontSize: 12 }} onClick={prevMonth} aria-label="Previous month">←</button>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {new Date(calYear, calMonth).toLocaleString('default', { month: 'short', year: 'numeric' })}
                  </span>
                  <button className="btn-ghost" style={{ padding: '4px 8px', fontSize: 12 }} onClick={nextMonth} aria-label="Next month">→</button>
                </div>
              )}
              <CalendarPreview
                selectedRows={displayOpt && displayOpt.row ? [displayOpt.row] : []}
                mode={calMode}
                year={calYear}
                month={calMonth}
              />
              <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10, textAlign: 'center' }}>
                {hoveredOpt ? 'Showing hovered option' : 'Hover an option to preview its recurrence'}
              </p>
            </div>

            {/* Always rendered — opacity hides it when nothing meaningful to show */}
            <div style={{ opacity: displayOpt && displayOpt.row ? 1 : 0, transition: 'opacity 0.2s', pointerEvents: displayOpt && displayOpt.row ? 'auto' : 'none' }}>
              <DayColumnViz
                row={displayOpt ? displayOpt.row : (firstValidOpt ? firstValidOpt.row : null)}
                selectedTimes={displayOpt ? options.filter(o => o.row && o.row.id === displayOpt.row?.id).map(o => o.time) : []}
                duration={duration}
                use12h={use12h}
                voterData={voterDataForRow}
                hoveredTime={hoveredOpt ? hoveredOpt.time : null}
                pollTz={poll.timezone}
                displayTz={timezone}
              />
            </div>
          </div>
          )}
        </div>
      )}

      {/* ── RESULTS TAB ──────────────────────────────────────────────────────── */}
      {activeTab === 'results' && (
        <div className="fade-up poll-layout">
          <div>

          {/* Results timezone selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            <label htmlFor="results-tz-select" style={{ fontSize: 13, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
              Times shown in:
            </label>
            <select
              id="results-tz-select"
              value={resultsTz}
              onChange={e => setResultsTz(e.target.value)}
              style={{ fontSize: 13, padding: '6px 10px', width: 'auto' }}
              aria-label="Timezone for results display"
            >
              {/* If browser tz not in list, show it at top */}
              {!TIMEZONES.find(tz => tz.value === resultsTz) && (
                <option value={resultsTz}>{resultsTz}</option>
              )}
              {TIMEZONES.map(tz => (
                <option
                  key={tz.value}
                  value={tz.value}
                  style={tz.value === poll.timezone ? {
                    fontWeight: 700,
                    background: 'rgba(124,111,247,0.2)',
                  } : {}}
                >
                  {tz.value === poll.timezone ? '★ ' : ''}{tz.label}
                </option>
              ))}
            </select>
            {resultsTz !== poll.timezone && (
              <button
                onClick={() => setResultsTz(poll.timezone)}
                style={{
                  fontSize: 11, color: 'var(--accent2)', background: 'none',
                  border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0,
                }}
                aria-label="Switch to organizer's timezone"
              >
                ★ organizer's tz
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 28 }}>
            {participants.length === 0
              ? <span style={{ color: 'var(--muted)', fontSize: 14 }}>No votes yet — share the poll link!</span>
              : participants.map(p => {
                  const isPinned = pinnedParticipants.has(p);
                  return (
                    <button
                      key={p}
                      onClick={() => setPinnedParticipants(prev => {
                        const next = new Set(prev);
                        if (next.has(p)) next.delete(p); else next.add(p);
                        return next;
                      })}
                      aria-pressed={isPinned}
                      className="pill"
                      style={{
                        background: isPinned ? 'rgba(124,111,247,0.18)' : 'var(--surface2)',
                        border: '1px solid ' + (isPinned ? 'var(--accent)' : 'var(--border2)'),
                        color: isPinned ? 'var(--accent)' : 'var(--text)',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'all 0.15s',
                        boxShadow: isPinned ? '0 0 0 2px rgba(124,111,247,0.2)' : 'none',
                        font: 'inherit',
                      }}
                    >
                      {p}
                    </button>
                  );
                })
            }
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {options.map((opt) => {
              const idx   = opt.index;
              const yes   = yesFor(idx);
              const no    = noFor(idx);
              const inb   = ifNeedBeFor(idx);
              const dnv   = didntVoteFor(idx);
              const total = participants.length;
              const pct   = total > 0 ? Math.round(yes.length / total * 100) : 0;
              const isTop = yes.length === maxYes && maxYes > 0;
              const isPinnedMatch = pinnedAllYes(idx);

              return (
                <div
                  key={idx}
                  className="card"
                  onMouseEnter={() => setHoveredOpt(opt)}
                  onMouseLeave={() => setHoveredOpt(null)}
                  style={{
                    border: '1px solid ' + (isPinnedMatch ? 'var(--accent)' : isTop ? 'var(--green)' : 'var(--border)'),
                    background: isPinnedMatch ? 'rgba(124,111,247,0.05)' : isTop ? 'rgba(68,215,168,0.03)' : 'var(--surface)',
                    boxShadow: isPinnedMatch ? '0 0 0 2px rgba(124,111,247,0.15)' : 'none',
                    cursor: 'default',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14, gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
                        {resultsLabel(opt)}
                      </div>
                      {isTop && <span className="tag tag-winner">⭐️ Most popular</span>}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 26, fontWeight: 700, color: isTop ? 'var(--green)' : 'var(--text)' }}>
                        {pct}%
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>yes rate</div>
                    </div>
                  </div>

                  <div style={{ background: 'var(--surface2)', borderRadius: 4, height: 7, marginBottom: 16, overflow: 'hidden', display: 'flex' }}>
                    <div style={{
                      height: '100%', borderRadius: '4px 0 0 4px', transition: 'width 0.5s ease',
                      background: isTop ? 'var(--green)' : 'var(--accent)',
                      width: pct + '%',
                    }} />
                    {dnv.length > 0 && (
                      <div style={{
                        height: '100%', transition: 'width 0.5s ease',
                        background: 'var(--border2)',
                        width: Math.round(dnv.length / total * 100) + '%',
                        opacity: 0.7,
                      }} />
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    {[
                      { list: yes, label: '✓ Yes',          cls: 'tag-yes',       names: yes.map(v => v.participant_name) },
                      { list: inb, label: '~ If need be',   cls: 'tag-ifneedbe',  names: inb.map(v => v.participant_name) },
                      { list: no,  label: '✗ No',           cls: 'tag-no',        names: no.map(v => v.participant_name) },
                      { list: dnv.map(n => ({ participant_name: n })), label: '— No response', cls: 'tag-dnv', names: dnv },
                    ].map(({ list, label, cls, names }) => list.length === 0 ? null : (
                      <div key={label}>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
                          {label} ({list.length})
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {names.map(n => (
                            <span key={n} className={'tag ' + cls}>{n}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          </div>

          {/* Results sidebar */}
          <div className="poll-sidebar">
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>Pattern preview</span>
                <div className="toggle-wrap" role="group" aria-label="Calendar view mode">
                  <button className={'toggle-opt' + (calMode === 'abstract' ? ' active' : '')} onClick={() => setCalMode('abstract')} aria-pressed={calMode === 'abstract'} style={{ fontSize: 11, padding: '4px 8px' }}>Abstract</button>
                  <button className={'toggle-opt' + (calMode === 'real'     ? ' active' : '')} onClick={() => setCalMode('real')}     aria-pressed={calMode === 'real'}     style={{ fontSize: 11, padding: '4px 8px' }}>Real</button>
                </div>
              </div>
              {calMode === 'real' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <button className="btn-ghost" style={{ padding: '4px 8px', fontSize: 12 }} onClick={prevMonth} aria-label="Previous month">←</button>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {new Date(calYear, calMonth).toLocaleString('default', { month: 'short', year: 'numeric' })}
                  </span>
                  <button className="btn-ghost" style={{ padding: '4px 8px', fontSize: 12 }} onClick={nextMonth} aria-label="Next month">→</button>
                </div>
              )}
              <CalendarPreview
                selectedRows={displayOpt && displayOpt.row ? [displayOpt.row] : []}
                mode={calMode}
                year={calYear}
                month={calMonth}
              />
              <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10, textAlign: 'center' }}>
                {hoveredOpt ? 'Showing hovered option' : 'Hover an option to preview its recurrence'}
              </p>
            </div>

            <div style={{ opacity: displayOpt && displayOpt.row ? 1 : 0, transition: 'opacity 0.2s', pointerEvents: displayOpt && displayOpt.row ? 'auto' : 'none' }}>
              <DayColumnViz
                row={displayOpt ? displayOpt.row : (firstValidOpt ? firstValidOpt.row : null)}
                selectedTimes={displayOpt ? options.filter(o => o.row && o.row.id === displayOpt.row?.id).map(o => o.time) : []}
                duration={duration}
                use12h={use12h}
                voterData={voterDataForRow}
                hoveredTime={hoveredOpt ? hoveredOpt.time : null}
                pollTz={poll.timezone}
                displayTz={resultsTz}
              />
            </div>
          </div>
        </div>
      )}

      <Footer showDonate={activeTab === 'results'} />
    </main>
  );
}
